import moment from "moment";
import {
    ReferenceLinkedDocumentKind,
    StoredReference,
    generateReferenceId,
} from "@utils/DocumentReferences";
import { normalizeDocumentNumber } from "./loadEDocumentImportContext";
import { ParsedEDocument, ParsedReference, ReferenceableDocument } from "./types";

/** Los documentos que esta carga puede ligar son siempre facturas o tiquetes. */
const LINKED_DOCUMENT_KIND: ReferenceLinkedDocumentKind = "Invoice";

/**
 * Fecha de la referencia en RFC3339, que es lo que espera el emisor.
 *
 * El XML la trae ya con offset (`xs:dateTime`), pero un comprobante viejo puede traerla sin el;
 * en ese caso `moment` la interpreta en la zona local, que es la del tenant.
 */
const toRfc3339 = (raw: string): string => {
    const parsed = raw ? moment.parseZone(raw) : moment();
    return (parsed.isValid() ? parsed : moment()).format();
};

/**
 * Convierte una referencia del XML a la forma que el sistema persiste.
 *
 * `StoredReference` no es lo mismo que `ParsedReference`: agrega `id`, `source` y —lo que de
 * verdad importa— renombra la fecha a `fechaEmisionUTC`. `readStoredReferencesAsInfoRef`
 * (`@utils/EDocument`) descarta en silencio toda entrada que no traiga ese campo, asi que
 * guardar la forma del parser equivale a guardar un documento sin referencias.
 */
export const toStoredReference = (
    reference: ParsedReference,
    linkedDocument?: ReferenceableDocument
): StoredReference => ({
    id: generateReferenceId(),
    source: "auto",
    numero: reference.numero,
    fechaEmisionUTC: toRfc3339(reference.fechaEmision),
    tipoDoc: reference.tipoDoc,
    codigo: reference.codigo,
    razon: reference.razon,
    ...(linkedDocument
        ? { linkedDocumentId: linkedDocument.id, linkedDocumentKind: LINKED_DOCUMENT_KIND }
        : {}),
});

/**
 * Todas las referencias del comprobante, con el documento del sistema ligado en la que
 * efectivamente resolvio. Una nota puede declarar hasta 10 referencias y solo una apunta al
 * documento que se logro encontrar.
 */
export const toStoredReferences = (
    parsed: ParsedEDocument,
    linkedDocument?: ReferenceableDocument
): StoredReference[] => {
    const linkedNumbers = linkedDocument
        ? new Set(
              [linkedDocument.key, linkedDocument.sequence]
                  .map(normalizeDocumentNumber)
                  .filter(Boolean)
          )
        : new Set<string>();

    return parsed.referencias.map((reference) =>
        toStoredReference(
            reference,
            linkedNumbers.has(normalizeDocumentNumber(reference.numero)) ? linkedDocument : undefined
        )
    );
};

/** `info_references` listo para el modelo, o `undefined` cuando no hay ninguna. */
export const serializeParsedReferences = (
    parsed: ParsedEDocument,
    linkedDocument?: ReferenceableDocument
): string | undefined => {
    const references = toStoredReferences(parsed, linkedDocument);
    return references.length ? JSON.stringify(references) : undefined;
};
